import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import ProductCard from "@/components/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

const CONDITIONS = ["Nuovo", "Usato", "Vintage"];

export default function Catalog() {
  const { slug } = useParams();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentCat, setCurrentCat] = useState(null);
  const [sort, setSort] = useState("newest");
  const [condition, setCondition] = useState("all");

  useEffect(() => {
    (async () => {
      setLoading(true);
      const params = {};
      if (slug) params.category_slug = slug;
      if (sort !== "newest") params.sort = sort;
      if (condition !== "all") params.condition = condition;
      const [pr, cr] = await Promise.all([
        api.get("/products", { params }),
        api.get("/categories"),
      ]);
      setProducts(pr.data);
      setCats(cr.data);
      setCurrentCat(cr.data.find((c) => c.slug === slug) || null);
      setLoading(false);
    })();
  }, [slug, sort, condition]);

  const roots = cats.filter((c) => !c.parent_id);
  const isRoot = currentCat && !currentCat.parent_id;
  const activeRootId = currentCat ? (currentCat.parent_id || currentCat.id) : null;
  const subsOfActiveRoot = activeRootId ? cats.filter((c) => c.parent_id === activeRootId) : [];

  return (
    <Layout>
      <div className="px-4 pt-6">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Link to="/catalogo" className="hover:text-brand-blue" data-testid="crumb-all">Catalogo</Link>
          {currentCat && !isRoot && currentCat.parent_id && (
            <>
              <span>/</span>
              <Link
                to={`/catalogo/${(cats.find((c) => c.id === currentCat.parent_id) || {}).slug || ""}`}
                className="hover:text-brand-blue"
                data-testid="crumb-parent"
              >
                {(cats.find((c) => c.id === currentCat.parent_id) || {}).name}
              </Link>
            </>
          )}
          {currentCat && (
            <>
              <span>/</span>
              <span className="text-slate-900 font-bold">{currentCat.name}</span>
            </>
          )}
        </div>

        <h1 className="font-heading font-black text-2xl sm:text-3xl text-brand-blue mt-2">
          {currentCat ? currentCat.name : "Catalogo completo"}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {products.length} {products.length === 1 ? "prodotto" : "prodotti"}
        </p>

        {/* Root chips */}
        <div className="scroll-row flex gap-2 overflow-x-auto pb-2 mt-4">
          <Link
            to="/catalogo"
            data-testid="chip-all"
            className={`whitespace-nowrap px-4 py-2 rounded-full text-xs font-bold border transition ${
              !slug ? "bg-brand-blue text-white border-brand-blue" : "bg-white text-slate-700 border-border"
            }`}
          >
            Tutti
          </Link>
          {roots.map((c) => (
            <Link
              key={c.id}
              to={`/catalogo/${c.slug}`}
              data-testid={`chip-${c.slug}`}
              className={`whitespace-nowrap px-4 py-2 rounded-full text-xs font-bold border transition ${
                activeRootId === c.id
                  ? "bg-brand-blue text-white border-brand-blue"
                  : "bg-white text-slate-700 border-border"
              }`}
            >
              {c.name}
            </Link>
          ))}
        </div>

        {/* Subcategory chips */}
        {subsOfActiveRoot.length > 0 && (
          <div className="scroll-row flex gap-2 overflow-x-auto pb-2 mt-2">
            {subsOfActiveRoot.map((s) => (
              <Link
                key={s.id}
                to={`/catalogo/${s.slug}`}
                data-testid={`subchip-${s.slug}`}
                className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                  slug === s.slug
                    ? "bg-brand-yellow text-brand-blue border-brand-yellow"
                    : "bg-white text-slate-600 border-border"
                }`}
              >
                {s.name}
              </Link>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mt-3">
          <Select value={condition} onValueChange={setCondition}>
            <SelectTrigger data-testid="filter-condition" className="w-40 h-10 rounded-full">
              <SelectValue placeholder="Condizione" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le condizioni</SelectItem>
              {CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="filter-sort" className="w-48 h-10 rounded-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Nuovi arrivi</SelectItem>
              <SelectItem value="price_asc">Prezzo crescente</SelectItem>
              <SelectItem value="price_desc">Prezzo decrescente</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="mt-8 text-center text-slate-500">Caricamento...</div>
        ) : products.length === 0 ? (
          <div className="mt-16 text-center">
            <p className="text-slate-500">Nessun prodotto disponibile.</p>
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
